import { useState } from 'react';
import KPICard from './components/KPICard';
import LeadPipeline from './components/LeadPipeline';
import RecentBookings from './components/RecentBookings';
import TasksWidget from './components/TasksWidget';
import AIChat from './components/AIChat';
import OnboardingChecklist from './components/OnboardingChecklist';
import { kpiData, agencyInfo } from '../../mocks/dashboard';
import { useTenant } from '../../context/TenantContext';

interface QuickAction {
  label: string;
  icon: string;
  path: string;
  color: string;
}

const quickActions: QuickAction[] = [
  { label: 'New Lead', icon: 'ri-user-add-line', path: '/crm/leads', color: '#0D9488' },
  { label: 'New Booking', icon: 'ri-calendar-check-line', path: '/bookings/new', color: '#059669' },
  { label: 'New Invoice', icon: 'ri-bill-line', path: '/invoices/new', color: '#D97706' },
  { label: 'New Itinerary', icon: 'ri-map-2-line', path: '/itinerary', color: '#7C3AED' },
];

export default function DashboardPage() {
  const [showQuick, setShowQuick] = useState(false);
  const { currentUser, agencyProfile } = useTenant();
  const brand = agencyProfile.brandColor || agencyInfo.brandColor;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = (currentUser.name && currentUser.name !== 'User')
    ? currentUser.name.split(' ')[0]
    : 'there';

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const agencyDisplayName = (agencyProfile.name && agencyProfile.name !== 'My Agency')
    ? agencyProfile.name
    : agencyInfo.name;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{greeting}, {firstName} 👋</h1>
          <p className="text-sm text-gray-400 mt-0.5">{today} · {agencyDisplayName} Dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-download-2-line" />
            Export Report
          </button>
          <button
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer whitespace-nowrap transition-all hover:opacity-90"
            style={{ backgroundColor: brand }}
            onClick={() => setShowQuick((v) => !v)}
          >
            <i className="ri-add-line" />
            Quick Add
          </button>
        </div>
      </div>

      {/* Quick action dropdown */}
      {showQuick && (
        <div className="grid grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <a
              key={a.label}
              href={a.path}
              className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-gray-100 hover:border-gray-200 cursor-pointer transition-all group"
            >
              <div
                className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${a.color}15` }}
              >
                <i className={`${a.icon} text-lg`} style={{ color: a.color }} />
              </div>
              <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">{a.label}</span>
            </a>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        {kpiData.map((kpi) => (
          <KPICard key={kpi.id} {...kpi} />
        ))}
      </div>

      {/* Onboarding Checklist */}
      <OnboardingChecklist />

      {/* Revenue chart + pipeline */}
      <div className="grid grid-cols-3 gap-4">
        {/* Revenue mini chart */}
        <div className="col-span-1 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Revenue Trend</h3>
              <p className="text-xs text-gray-400 mt-0.5">Last 7 months</p>
            </div>
            <select className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 focus:outline-none cursor-pointer">
              <option>2026</option>
              <option>2025</option>
            </select>
          </div>
          <RevenueChart brandColor={brand} />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">This Month</p>
              <p className="text-sm font-bold text-gray-800">AED 284,750</p>
              <p className="text-xs text-green-500 font-medium mt-0.5">↑ 12.4%</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Target</p>
              <p className="text-sm font-bold text-gray-800">AED 320,000</p>
              <p className="text-xs text-amber-500 font-medium mt-0.5">89% achieved</p>
            </div>
          </div>
        </div>

        {/* Top services */}
        <div className="col-span-1 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Top Services</h3>
            <span className="text-xs text-gray-400">This month</span>
          </div>
          <ServiceBreakdown brandColor={brand} />
        </div>

        {/* Lead sources */}
        <div className="col-span-1 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Lead Sources</h3>
            <span className="text-xs text-gray-400">This month</span>
          </div>
          <LeadSources brandColor={brand} />
        </div>
      </div>

      {/* Lead Pipeline */}
      <LeadPipeline />

      {/* Bottom row: Bookings + Tasks + AI Chat */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <RecentBookings />
        </div>
        <div className="col-span-1 flex flex-col gap-4">
          <TasksWidget />
          <AIChat />
        </div>
      </div>
    </div>
  );
}

/* ── Internal chart components ─────────────────────── */

function RevenueChart({ brandColor }: { brandColor: string }) {
  const data = [120, 180, 145, 210, 195, 250, 285];
  const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
  const max = Math.max(...data);
  const h = 100;
  const w = 200;

  const points = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`)
    .join(' ');

  const area = `${points} ${w},${h} 0,${h}`;

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h + 20}`} className="overflow-visible">
      <defs>
        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={brandColor} stopOpacity="0.2" />
          <stop offset="100%" stopColor={brandColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#revGrad)" />
      <polyline
        points={points}
        fill="none"
        stroke={brandColor}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {data.map((v, i) => (
        <circle
          key={i}
          cx={(i / (data.length - 1)) * w}
          cy={h - (v / max) * h}
          r="3"
          fill="white"
          stroke={brandColor}
          strokeWidth="2"
        />
      ))}
      {months.map((m, i) => (
        <text
          key={m}
          x={(i / (data.length - 1)) * w}
          y={h + 16}
          textAnchor="middle"
          className="text-[10px]"
          fill="#9CA3AF"
          fontSize="9"
        >
          {m}
        </text>
      ))}
    </svg>
  );
}

function ServiceBreakdown({ brandColor }: { brandColor: string }) {
  const services = [
    { name: 'Holiday Packages', pct: 38, color: brandColor },
    { name: 'Flight Bookings', pct: 24, color: '#059669' },
    { name: 'Hotel Only', pct: 17, color: '#D97706' },
    { name: 'Visa Services', pct: 12, color: '#7C3AED' },
    { name: 'Transfers', pct: 9, color: '#6B7280' },
  ];

  return (
    <div className="space-y-3">
      {services.map((s) => (
        <div key={s.name}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">{s.name}</span>
            <span className="text-xs font-semibold text-gray-700">{s.pct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${s.pct}%`, backgroundColor: s.color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadSources({ brandColor }: { brandColor: string }) {
  const sources = [
    { name: 'WhatsApp', count: 84, pct: 38, icon: 'ri-whatsapp-line', color: '#25D366' },
    { name: 'Website Form', count: 56, pct: 25, icon: 'ri-global-line', color: brandColor },
    { name: 'Referral', count: 38, pct: 17, icon: 'ri-share-line', color: '#D97706' },
    { name: 'Walk-in', count: 28, pct: 13, icon: 'ri-store-line', color: '#7C3AED' },
    { name: 'Phone Call', count: 16, pct: 7, icon: 'ri-phone-line', color: '#6B7280' },
  ];

  return (
    <div className="space-y-2.5">
      {sources.map((s) => (
        <div key={s.name} className="flex items-center gap-3">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ backgroundColor: `${s.color}15` }}
          >
            <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600 truncate">{s.name}</span>
              <span className="text-xs font-bold text-gray-700 ml-2">{s.count}</span>
            </div>
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${s.pct}%`, backgroundColor: s.color }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
